// FASE 05 — Cadastros Gerais, Referências (plano de 23/09/2026, Frentes C1/C2).
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, CloudUpload, Delete } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigation } from "contexts/notificationsContext";
import {
  buscarCadastro,
  excluirCadastro,
  listarTodos,
  salvarCadastro,
  TIPOS_MATERIAL,
} from "services/modules/cadastrosGerais";

interface IVinculoLinha {
  idReferenciaLinha: number;
  idLinha: number;
  descricao: string;
  // Decisão P3 (02/10/2026): a caixa "Gerar no pacote da referência" saiu desta tela — o controle do pacote N1
  // voltou para o switch do Cadastro de Linhas. A coluna ReferenciasLinhas.PacoteReferencia continua no banco
  // (o "Adicionar" grava true) porque alimenta a lista de linhas bloqueadas do CEM MOBI; o endpoint
  // PUT /referencia/linha/pacote-referencia fica no backend, sem chamada da tela.
  pacoteReferencia: boolean;
}

interface IVinculoClasse {
  idReferenciaClasseId: number;
  idClasseId: number;
  codigo: string;
  descricao: string;
}

interface IReferencia {
  idReferencia: number;
  descricao: string;
  logo?: string;
  perfil: boolean;
  componente: boolean;
  vidro: boolean;
  superficie: boolean;
  kit: boolean;
  linhas: IVinculoLinha[];
  classesIds: IVinculoClasse[];
}

interface IOpcao {
  id: number;
  label: string;
}

interface ModalReferenciaProps {
  aberto: boolean;
  idReferencia?: number;
  onFechar: (houveAlteracao: boolean) => void;
}

// A logo passa pelo serviço de arquivos do MOF novo, que aceita até 800 KB.
const TAMANHO_MAXIMO_LOGO = 800 * 1024;
const TIPOS_VAZIOS = { perfil: false, componente: false, vidro: false, superficie: false, kit: false };

export function ModalReferencia({ aberto, idReferencia, onFechar }: ModalReferenciaProps) {
  const { openNotification } = useNavigation();
  const [id, setId] = useState<number | undefined>(idReferencia);
  const [descricao, setDescricao] = useState("");
  const [tipos, setTipos] = useState<Record<string, boolean>>(TIPOS_VAZIOS);
  const [logo, setLogo] = useState("");
  const [logoAlterada, setLogoAlterada] = useState(false);
  const [logoFormato, setLogoFormato] = useState("");
  const [vinculosLinhas, setVinculosLinhas] = useState<IVinculoLinha[]>([]);
  const [vinculosClasses, setVinculosClasses] = useState<IVinculoClasse[]>([]);
  const [opcoesLinhas, setOpcoesLinhas] = useState<IOpcao[]>([]);
  const [opcoesClasses, setOpcoesClasses] = useState<IOpcao[]>([]);
  const [linhaEscolhida, setLinhaEscolhida] = useState<IOpcao | null>(null);
  const [classeEscolhida, setClasseEscolhida] = useState<IOpcao | null>(null);
  const [erros, setErros] = useState<{ descricao?: string; tipos?: string }>({});
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [houveAlteracao, setHouveAlteracao] = useState(false);

  const aplicar = (referencia: IReferencia | null) => {
    setDescricao(referencia?.descricao ?? "");
    setTipos(
      referencia
        ? {
            perfil: referencia.perfil,
            componente: referencia.componente,
            vidro: referencia.vidro,
            superficie: referencia.superficie,
            kit: referencia.kit,
          }
        : TIPOS_VAZIOS
    );
    setLogo(referencia?.logo ?? "");
    setLogoAlterada(false);
    setLogoFormato("");
    setVinculosLinhas(referencia?.linhas ?? []);
    setVinculosClasses(referencia?.classesIds ?? []);
  };

  const recarregar = async (idAtual: number) => {
    const referencia = await buscarCadastro<IReferencia>("/Referencia", idAtual);
    aplicar(referencia);
  };

  // Depois de mexer num vínculo, só as listas de vínculos voltam do servidor: nome, tipos e logo ainda
  // não salvos pelo usuário continuam na tela.
  const recarregarVinculos = async (idAtual: number) => {
    const referencia = await buscarCadastro<IReferencia>("/Referencia", idAtual);
    setVinculosLinhas(referencia?.linhas ?? []);
    setVinculosClasses(referencia?.classesIds ?? []);
  };

  useEffect(() => {
    if (!aberto) return;
    setId(idReferencia);
    setErros({});
    setHouveAlteracao(false);
    setLinhaEscolhida(null);
    setClasseEscolhida(null);
    aplicar(null);

    const carregar = async () => {
      setCarregando(true);
      try {
        const [linhas, classes] = await Promise.all([listarTodos<any>("/Linha"), listarTodos<any>("/ClasseId")]);
        setOpcoesLinhas(linhas.map((l) => ({ id: l.idLinha, label: l.descricao })));
        setOpcoesClasses(classes.map((c) => ({ id: c.idClasseId, label: `${c.codigo} — ${c.descricao}` })));
        if (idReferencia) await recarregar(idReferencia);
      } catch (error: any) {
        openNotification({ message: error?.message || "Não foi possível carregar a referência", type: "error" });
      } finally {
        setCarregando(false);
      }
    };
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, idReferencia]);

  const selecionarLogo = (evento: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!arquivo) return;
    if (arquivo.size > TAMANHO_MAXIMO_LOGO) {
      openNotification({ message: "A logo deve ter no máximo 800 KB.", type: "error" });
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => {
      setLogo(String(leitor.result));
      setLogoFormato((arquivo.name.split(".").pop() || "png").toLowerCase());
      setLogoAlterada(true);
    };
    leitor.readAsDataURL(arquivo);
  };

  const salvar = async () => {
    const novosErros: typeof erros = {};
    if (!descricao.trim()) novosErros.descricao = "Campo obrigatório";
    if (!Object.values(tipos).some(Boolean)) novosErros.tipos = "Selecione pelo menos um tipo de produto";
    setErros(novosErros);
    if (Object.keys(novosErros).length) return;

    setSalvando(true);
    try {
      const resposta = await salvarCadastro<{ idReferencia: number }>("/Referencia", {
        idReferencia: id ?? 0,
        descricao: descricao.trim(),
        ...tipos,
        logoAlterada,
        logoBase64: logoAlterada ? logo : undefined,
        logoFormato: logoAlterada ? logoFormato : undefined,
      });
      setHouveAlteracao(true);
      const idGravado = resposta?.idReferencia ?? id;
      if (!id && idGravado) {
        // Referência nova: fica aberta para vincular linhas e ClassesIds.
        setId(idGravado);
        await recarregar(idGravado);
        openNotification({ message: "Referência cadastrada. Agora vincule as linhas e as ClassesIds.", type: "success" });
        return;
      }
      openNotification({ message: "Referência salva com sucesso!", type: "success" });
      onFechar(true);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível salvar a referência", type: "error" });
    } finally {
      setSalvando(false);
    }
  };

  const executarVinculo = async (acao: () => Promise<unknown>, sucesso: string) => {
    if (!id) return;
    setCarregando(true);
    try {
      await acao();
      setHouveAlteracao(true);
      await recarregarVinculos(id);
      openNotification({ message: sucesso, type: "success" });
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível alterar o vínculo", type: "error" });
    } finally {
      setCarregando(false);
    }
  };

  const tiposSelecionados = TIPOS_MATERIAL.filter(({ campo }) => tipos[campo]);

  return (
    <Dialog open={aberto} onClose={() => onFechar(houveAlteracao)} maxWidth="md" fullWidth>
      <DialogTitle>
        Cadastro de referências
        <IconButton
          onClick={() => onFechar(houveAlteracao)}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome da referência"
              value={descricao}
              onChange={(evento) => setDescricao(evento.target.value)}
              error={!!erros.descricao}
              helperText={erros.descricao ?? "Obrigatório"}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 90,
                  height: 60,
                  border: "1px dashed",
                  borderColor: "grey.400",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {logo ? (
                  <img src={logo} alt="Logo da referência" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Sem logo
                  </Typography>
                )}
              </Box>
              <input
                accept=".png,.jpg,.jpeg,.svg"
                style={{ display: "none" }}
                id="logo-referencia-input"
                type="file"
                onChange={selecionarLogo}
              />
              <label htmlFor="logo-referencia-input">
                <LoadingButton component="span" variant="outlined" startIcon={<CloudUpload />}>
                  Enviar logo
                </LoadingButton>
              </label>
              {logo && (
                <Tooltip title="Remover logo">
                  <IconButton
                    color="error"
                    onClick={() => {
                      setLogo("");
                      setLogoAlterada(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Formatos aceitos: PNG, JPG, SVG (máx. 800 KB)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={[...TIPOS_MATERIAL]}
              value={tiposSelecionados}
              getOptionLabel={(opcao) => opcao.label}
              isOptionEqualToValue={(opcao, valor) => opcao.campo === valor.campo}
              onChange={(_, novos) => {
                const marcados = new Set(novos.map((n) => n.campo));
                setTipos(Object.fromEntries(TIPOS_MATERIAL.map(({ campo }) => [campo, marcados.has(campo)])));
              }}
              renderTags={(marcados, getTagProps) =>
                marcados.map((opcao, indice) => (
                  <Chip label={opcao.label} color="primary" {...getTagProps({ index: indice })} key={opcao.campo} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipo de produto"
                  error={!!erros.tipos}
                  helperText={erros.tipos ?? "Selecione pelo menos um"}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Linhas vinculadas à referência
            </Typography>
            {!id ? (
              <Typography variant="caption" color="text.secondary">
                Salve a referência para vincular linhas.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={opcoesLinhas.filter((o) => !vinculosLinhas.some((v) => v.idLinha === o.id))}
                    value={linhaEscolhida}
                    onChange={(_, opcao) => setLinhaEscolhida(opcao)}
                    getOptionLabel={(opcao) => opcao.label}
                    isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                    renderInput={(params) => <TextField {...params} label="Selecionar linha para adicionar" />}
                  />
                  <LoadingButton
                    variant="contained"
                    startIcon={<Add />}
                    disabled={!linhaEscolhida}
                    loading={carregando}
                    onClick={() =>
                      executarVinculo(async () => {
                        await salvarCadastro("/referencia/linha", {
                          idReferencia: id,
                          idLinha: linhaEscolhida!.id,
                          pacoteReferencia: true,
                        });
                        setLinhaEscolhida(null);
                      }, "Linha vinculada à referência.")
                    }
                  >
                    Adicionar
                  </LoadingButton>
                </Box>
                <List dense>
                  {vinculosLinhas.map((vinculo) => (
                    <ListItem
                      key={vinculo.idReferenciaLinha}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          color="error"
                          disabled={carregando}
                          onClick={() =>
                            executarVinculo(
                              () => excluirCadastro("/referencia/linha", "idReferenciaLinha", vinculo.idReferenciaLinha),
                              "Linha desvinculada da referência."
                            )
                          }
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={vinculo.descricao} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              ClassesIds vinculadas à referência
            </Typography>
            {!id ? (
              <Typography variant="caption" color="text.secondary">
                Salve a referência para vincular ClassesIds.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={opcoesClasses.filter((o) => !vinculosClasses.some((v) => v.idClasseId === o.id))}
                    value={classeEscolhida}
                    onChange={(_, opcao) => setClasseEscolhida(opcao)}
                    getOptionLabel={(opcao) => opcao.label}
                    isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                    renderInput={(params) => <TextField {...params} label="Faça uma busca" />}
                  />
                  <LoadingButton
                    variant="contained"
                    startIcon={<Add />}
                    disabled={!classeEscolhida}
                    loading={carregando}
                    onClick={() =>
                      executarVinculo(async () => {
                        await salvarCadastro("/referencia/classeid", {
                          idReferencia: id,
                          idClasseId: classeEscolhida!.id,
                        });
                        setClasseEscolhida(null);
                      }, "ClasseId vinculada à referência.")
                    }
                  >
                    Adicionar
                  </LoadingButton>
                </Box>
                <List dense>
                  {vinculosClasses.map((vinculo) => (
                    <ListItem
                      key={vinculo.idReferenciaClasseId}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          color="error"
                          disabled={carregando}
                          onClick={() =>
                            executarVinculo(
                              () =>
                                excluirCadastro("/referencia/classeid", "idReferenciaClasseId", vinculo.idReferenciaClasseId),
                              "ClasseId desvinculada da referência."
                            )
                          }
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={`${vinculo.codigo} — ${vinculo.descricao}`} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <Grid container justifyContent="flex-end" spacing={2} sx={{ p: 2 }}>
        <Grid item>
          <LoadingButton variant="outlined" onClick={() => onFechar(houveAlteracao)} disabled={salvando}>
            {id ? "Fechar" : "Cancelar"}
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton variant="contained" onClick={salvar} loading={salvando} disabled={carregando}>
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
    </Dialog>
  );
}
